import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { PowerBIService } from '@/services/powerBIService';
import type { PowerBIState, PowerBIReport, EmbedConfig } from '@/types/powerBI.types';
import { UserRole } from '@/permissions/types';

interface PowerBIStore extends PowerBIState {
  // Actions
  fetchAllReports: (userRoles: UserRole[]) => Promise<void>;
  selectReport: (report: PowerBIReport) => void;
  generateEmbedToken: (
    reportId: string,
    type: 'report' | 'dashboard',
    endpoint?: string
  ) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const usePowerBIStore = create<PowerBIStore>()(
  devtools(
    (set) => ({
      // Initial state
      reports: [],
      selectedReport: null,
      embedConfig: null,
      loading: false,
      error: null,

      // Fetch all available reports based on user permissions
      fetchAllReports: async (userRoles: UserRole[]) => {
        set({ loading: true, error: null });

        try {
          const requests: Promise<PowerBIReport[]>[] = [];

          if (userRoles.includes(UserRole.POWERBI_BASIC)) {
            requests.push(PowerBIService.getReports());
          }

          if (userRoles.includes(UserRole.POWERBI_PRIVILEGED)) {
            requests.push(PowerBIService.getReportsPrivileged());
          }

          if (userRoles.includes(UserRole.POWERBI_PRIVILEGED_KPC)) {
            requests.push(PowerBIService.getReportsPrivilegedKPC());
          }

          const results = await Promise.all(requests);
          const allReports = results.flat();

          set({ reports: allReports, loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch reports',
          });
        }
      },

      // Select a report
      selectReport: (report: PowerBIReport) => {
        set({ selectedReport: report });
      },

      // Generate embed token and config
      generateEmbedToken: async (reportId, type, endpoint) => {
        set({ loading: true, error: null });

        try {
          const response = await PowerBIService.generateEmbedToken(
            reportId,
            type,
            endpoint || 'generateembedtoken'
          );

          const embedConfig: EmbedConfig = {
            id: response.embedReport[0].reportId,
            embedUrl: response.embedReport[0].embedUrl,
            accessToken: response.embedToken.token,
          };

          set({ embedConfig, loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to generate embed token',
          });
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Reset store
      reset: () => {
        set({
          reports: [],
          selectedReport: null,
          embedConfig: null,
          loading: false,
          error: null,
        });
      },
    }),
    { name: 'PowerBIStore' }
  )
);
